/**
 * @component
 * @description Submission modal useful when user need to display icon title subtitle in a default size modal.
 * @property {JSX.Element} Title Styled component to stye the title
 * @property {string} title Title to be displayed on the modal
 * @property {string} subtitle Subtitle to be showed after the title
 * @property {string} iconName Icon to be shown before the title
 * @property {string} iconColor Color of the icon to be displayed
 * @property {number} iconWidth Size of the icon
 * @property {boolean} disableOutsideClick Flag to check if outside click is disabled or not
 * @return {JSX.Element} Returns a modal with icon if required and title and subtitle in a default size modal
 */
import styled from "styled-components";
import PropTypes from "prop-types";

import { Spacing, Colors } from "@paclife/plds.tokens";
import { Icon } from "@paclife/plds.icon";

import BaseModal from "common/Modal/BaseModal";
import Typography from "common/Typography/Typography";
import Message from "common/Message/Message";

const ModalContent = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const Title = styled(Typography)`
    margin-bottom: ${Spacing[24]};
`;

const Subtitle = styled(Typography)`
    margin-bottom: ${Spacing[24]};
    text-align: center;
`;
const IconWrapper = styled(Icon)`
    margin-bottom: ${Spacing[24]};
`;

const SubmissionModal = ({
    title,
    subtitle,
    instruction,
    iconName,
    iconColor,
    Title,
    disableOutsideClick,
    iconWidth,
    error,
    ...baseModalProps
}) => {
    return (
        <BaseModal
            disableOutsideClick={disableOutsideClick}
            {...baseModalProps}
        >
            <ModalContent>
                {iconName && (
                    <IconWrapper
                        width={iconWidth}
                        name={iconName}
                        color={iconColor}
                    />
                )}
                <Title bold variant="h2">
                    {title}
                </Title>
                <Subtitle>{subtitle}</Subtitle>
                <Subtitle>{instruction}</Subtitle>
                {error && <Message type="error" message={error} />}
            </ModalContent>
        </BaseModal>
    );
};

export default SubmissionModal;

SubmissionModal.propTypes = {
    iconColor: PropTypes.string,
    iconName: PropTypes.string,
    iconWidth: PropTypes.number,
    Title: PropTypes.element,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    disableOutsideClick: PropTypes.bool
};

SubmissionModal.defaultProps = {
    Title: Title,
    disableOutsideClick: false,
    iconWidth: 42,
    iconColor: Colors.red600
};
